import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import confetti from "canvas-confetti";
import { FLOWER_PROMPTS, BOUQUET_HEADER, BOUQUET_SUB, BOUQUET_DONE } from "../MothersDayData";

const AVEN_CALM = process.env.PUBLIC_URL + "/mothersday/aven-cutout.png";
const AVEN_LAUGH = process.env.PUBLIC_URL + "/mothersday/aven-laughing-cutout.png";

const fireHeartConfetti = () => {
  const colors = ["#ff6b9d", "#c44569", "#ffd93d", "#ff1493", "#ffa07a"];
  confetti({ particleCount: 60, spread: 70, origin: { y: 0.6 }, colors, scalar: 1.05 });
  setTimeout(() => confetti({ particleCount: 35, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors }), 110);
  setTimeout(() => confetti({ particleCount: 35, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors }), 220);
};

const Flower = ({ color, index, total }) => {
  const spread = Math.max(1, total - 1);
  const t = total <= 1 ? 0.5 : index / spread;
  const angle = (t - 0.5) * 70;
  const x = (t - 0.5) * 95;
  const yOffset = -Math.abs(t - 0.5) * 18; // raise outer flowers for fan look

  return (
    <motion.div
      className="md-flower"
      style={{ marginLeft: -30 }}
      initial={{ x, y: 80, scale: 0, rotate: angle - 12, opacity: 0 }}
      animate={{ x, y: yOffset, scale: 1, rotate: angle, opacity: 1 }}
      transition={{ type: "spring", stiffness: 220, damping: 14, mass: 0.6 }}
    >
      <svg viewBox="0 0 70 110" xmlns="http://www.w3.org/2000/svg">
        <line x1="35" y1="110" x2="35" y2="55" stroke="#3a8045" strokeWidth="3" strokeLinecap="round" />
        <path d="M 35 90 Q 22 82 18 70 Q 30 78 35 90 Z" fill="#4ea35a" />
        <path d="M 35 78 Q 48 70 52 58 Q 40 66 35 78 Z" fill="#5fbf6c" />
        <g transform="translate(35 35)">
          {[0, 60, 120, 180, 240, 300].map((deg, i) => (
            <ellipse key={i} cx="0" cy="-14" rx="10" ry="16" fill={color} opacity="0.92" transform={`rotate(${deg})`} />
          ))}
          <circle cx="0" cy="0" r="7" fill="#ffd93d" />
        </g>
      </svg>
    </motion.div>
  );
};

const BouquetAct = ({ onNext }) => {
  const [planted, setPlanted] = useState([]);
  const [bubble, setBubble] = useState(null);
  const [bounce, setBounce] = useState(0);
  const [pose, setPose] = useState("calm"); // calm | laugh
  const [done, setDone] = useState(false);
  const bubbleTimer = useRef(null);

  const leftRail = FLOWER_PROMPTS.filter((_, i) => i % 2 === 0);
  const rightRail = FLOWER_PROMPTS.filter((_, i) => i % 2 === 1);

  const plant = (flower) => {
    if (planted.includes(flower.id) || done) return;
    setPlanted((p) => [...p, flower.id]);
    setBubble(flower.note);
    setBounce((b) => b + 1);
    setPose("laugh");
    fireHeartConfetti();
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => {
      setBubble(null);
      setPose("calm");
    }, 3800);

    if (planted.length + 1 >= FLOWER_PROMPTS.length) {
      setTimeout(() => setDone(true), 1500);
    }
  };

  // gentle continuous breathing on Aven
  const avenAnim = { y: [0, -6, 0], rotate: [-0.6, 0.6, -0.6] };

  return (
    <motion.div
      className="md-act md-act-bouquet"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.6 }}
    >
      <div className="md-bouquet-header">
        <h1>{BOUQUET_HEADER}</h1>
        <div className="md-sub">{BOUQUET_SUB} · {planted.length} / {FLOWER_PROMPTS.length}</div>
      </div>

      <div className="md-stage">
        <div className="md-prompts md-left">
          {leftRail.map((f) => (
            <button key={f.id} className="md-prompt-btn" onClick={() => plant(f)} disabled={planted.includes(f.id)}>
              {f.button}
            </button>
          ))}
        </div>

        <div className="md-center">
          <motion.div
            className="md-aven-wrap"
            animate={avenAnim}
            transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={pose + bounce}
                src={pose === "laugh" ? AVEN_LAUGH : AVEN_CALM}
                alt="Aven"
                className="md-aven"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={
                  bounce > 0
                    ? { opacity: 1, scale: [1, 1.06, 0.98, 1.03, 1], y: [0, -10, 0, -4, 0] }
                    : { opacity: 1, scale: 1 }
                }
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
              />
            </AnimatePresence>

            {/* eye blink overlay — only on calm pose */}
            {pose === "calm" && <div className="md-blink" />}

            <AnimatePresence>
              {bubble && (
                <motion.div
                  className="md-bubble"
                  initial={{ scale: 0.6, opacity: 0, y: 10 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.85, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 280, damping: 18 }}
                >
                  {bubble}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="md-bouquet">
              {planted.map((id, i) => {
                const f = FLOWER_PROMPTS.find((x) => x.id === id);
                return <Flower key={id} color={f.color} index={i} total={planted.length} />;
              })}
            </div>
          </motion.div>

          <div className="md-progress">
            {FLOWER_PROMPTS.map((f) => (
              <div key={f.id} className={`md-progress-dot ${planted.includes(f.id) ? "md-on" : ""}`} />
            ))}
          </div>
        </div>

        <div className="md-prompts md-right">
          {rightRail.map((f) => (
            <button key={f.id} className="md-prompt-btn" onClick={() => plant(f)} disabled={planted.includes(f.id)}>
              {f.button}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {done && (
          <motion.div
            className="md-act-bottom-cta"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <p className="md-finished-line">{BOUQUET_DONE}</p>
            <button className="md-btn-soft md-cta" onClick={onNext}>Next: little memory game →</button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default BouquetAct;
